/**
 * PRET-2045 — educational resources managed from the back office ("How To" content on mobile).
 * Contract: `/educational-resources` on the account-scoped API (see the PRET-2045 functional design).
 */

export type EducationalResourceType =
  | "VIDEO"
  | "AUDIO"
  | "PDF"
  | "WEB"
  | "IMAGE";

export const RESOURCE_TYPES: EducationalResourceType[] = [
  "VIDEO",
  "AUDIO",
  "PDF",
  "WEB",
  "IMAGE",
];

export const RESOURCE_TYPE_LABELS: Record<EducationalResourceType, string> = {
  VIDEO: "Video",
  AUDIO: "Audio",
  PDF: "PDF",
  WEB: "Web link",
  IMAGE: "Image",
};

/** Only these types carry a duration (BR-10). */
export const TYPES_WITH_DURATION: EducationalResourceType[] = [
  "VIDEO",
  "AUDIO",
];

export const hasDuration = (type: string | undefined | null): boolean =>
  TYPES_WITH_DURATION.includes(type as EducationalResourceType);

export type EducationalResourceStatus = "ACTIVE" | "INACTIVE";

export const RESOURCE_STATUSES: EducationalResourceStatus[] = [
  "ACTIVE",
  "INACTIVE",
];

export const RESOURCE_STATUS_LABELS: Record<EducationalResourceStatus, string> =
  {
    ACTIVE: "Active",
    INACTIVE: "Inactive",
  };

export type ResourceLanguageCode = "en-us" | "en-gb" | "es-419";

/** The language every resource must carry; its card is always first and locked (BR-06). */
export const DEFAULT_RESOURCE_LANGUAGE: ResourceLanguageCode = "en-us";

export const RESOURCE_LANGUAGES: ResourceLanguageCode[] = [
  "en-us",
  "en-gb",
  "es-419",
];

export const RESOURCE_LANGUAGE_LABELS: Record<ResourceLanguageCode, string> = {
  "en-us": "English (US)",
  "en-gb": "English (UK)",
  "es-419": "Spanish (Latin America)",
};

/** Codes are accepted case-insensitively and stored lowercase (BR-08). */
export const normalizeResourceLanguage = (
  raw: string | undefined | null
): ResourceLanguageCode | undefined => {
  if (!raw) return undefined;
  const lower = raw.replace("_", "-").toLowerCase();
  return RESOURCE_LANGUAGES.find((code) => code === lower);
};

export interface EducationalResourceLanguage {
  language: ResourceLanguageCode;
  title: string;
  description?: string;
  url: string;
}

export interface EducationalResourceAttributes {
  thumbnailUrl?: string;
  /** Seconds, VIDEO/AUDIO only. */
  duration?: number;
}

export interface EducationalResource {
  _id: string;
  accountId: string;
  categoryId: string;
  type: EducationalResourceType;
  status: EducationalResourceStatus;
  languages: EducationalResourceLanguage[];
  attributes?: EducationalResourceAttributes;
  isDeleted: boolean;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export type EducationalResourceSortField = "createdAt" | "type" | "status";

export interface EducationalResourceFilters {
  search: string;
  categoryId: string;
  type: EducationalResourceType | "";
  status: EducationalResourceStatus | "";
  /** 0-based in the UI; the service sends it 1-based. */
  pageNumber: number;
  pageSize: number;
  sortField: EducationalResourceSortField;
  sortOrder: "asc" | "desc";
}

export interface EducationalResourceList {
  list: EducationalResource[];
  totalCount: number;
}

export interface EducationalResourcePayload {
  categoryId: string;
  type: EducationalResourceType;
  status: EducationalResourceStatus;
  languages: EducationalResourceLanguage[];
  attributes?: EducationalResourceAttributes;
  createdBy?: string;
  updatedBy?: string;
}

/** Title shown in lists: the English (US) one, else the first, else "-". */
export const resourceDisplayTitle = (resource: EducationalResource): string => {
  const languages = resource.languages || [];
  const english = languages.find(
    (l) => normalizeResourceLanguage(l.language) === DEFAULT_RESOURCE_LANGUAGE
  );
  return english?.title || languages[0]?.title || "-";
};

/** Minimal shape of an EDUCATIONAL_RESOURCE category item, as returned by /categories/by-type. */
export interface ResourceCategoryItem {
  _id: string;
  status?: string;
  languages?: { language?: string; name?: string }[];
}

/**
 * Display name of a category item: the English (US) name, else the first one. Category language
 * codes are "en-US"/"en-GB"/"es-419" while resources use lowercase — compared case-insensitively.
 * Inactive items are suffixed " (inactive)".
 */
export const categoryItemName = (item: ResourceCategoryItem): string => {
  const languages = item.languages || [];
  const english = languages.find(
    (l) => (l.language || "").toLowerCase() === DEFAULT_RESOURCE_LANGUAGE
  );
  const name = english?.name || languages[0]?.name || "-";
  return item.status === "INACTIVE" ? `${name} (inactive)` : name;
};
